'use client';
import { useEffect, useLayoutEffect } from 'react';

/**
 * `useLayoutEffect` on the client, `useEffect` on the server — whichever is safe to call in the
 * current environment.
 *
 * React warns if `useLayoutEffect` runs during server rendering, since there's no DOM to
 * measure or mutate synchronously before paint. Swapping to `useEffect` on the server avoids
 * that warning while preserving `useLayoutEffect`'s timing (runs synchronously after DOM
 * mutations, before the browser paints) once the code reaches the client.
 *
 * Use this instead of `useLayoutEffect` directly in any hook or component that might render
 * during SSR — anything reading layout (`getBoundingClientRect`, `getComputedStyle`) or writing
 * to the DOM before paint to avoid a visible flicker.
 *
 * @example
 * ```tsx
 * useIsomorphicLayoutEffect(() => {
 *   // Runs synchronously before paint on the client; a normal (server-safe) effect during SSR.
 *   const { width } = element.getBoundingClientRect();
 *   setWidth(width);
 * }, [element]);
 * ```
 */
export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
